import classnames from 'classnames'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import useViewport from '../../../hooks/useViewport'
import {
  selectCorporateProfile,
  selectLoanApplicationCtaEnabled,
  selectLoanApplicationUri
} from '../../../store/selectors/customerProfile'
import { Avatar, Text } from '../..'
import NMLS from '../NMLS'
import CustomerTitle from '../CustomerTitle'
import { MobilePhone } from './MobilePhone'
import { MobileEmail } from './MobileEmail'
import DesktopPhone from './DesktopPhone'
import DesktopEmail from './DesktopEmail'
import { LeavingHomebotCtaButton } from '../LeavingHomebotCta/LeavingHomebotCtaButton'
import { Flex, Card } from '@homebotapp/hb-react-component-catalog'

import styles from './Intro.module.scss'

interface IntroProps {
  toName: string
  customerName: string
  photoUri: string
  email: string
  nmls: string
  phone: string
  title: string
  children: React.ReactNode
}

export const MSG = defineMessages({
  salutation: {
    id: 'Intro.salutation',
    defaultMessage: 'Hi {firstName} —'
  },
  ctaButtonText: {
    id: 'Intro.ctaButtonText',
    defaultMessage: 'Explore your financing options'
  }
})

export const Intro = ({ toName, customerName, phone, photoUri, email, nmls, title, children }: IntroProps) => {
  const intl = useIntl()
  const { isMedium } = useViewport()
  const corporateProfile = useSelector(selectCorporateProfile) || {}
  const loanApplicationUri = useSelector(selectLoanApplicationUri)
  const loanApplicatioCtaEnabled = useSelector(selectLoanApplicationCtaEnabled)
  const { avatarUri: companyLogo } = corporateProfile
  const PhoneComponent = isMedium ? MobilePhone : DesktopPhone
  const EmailComponent = isMedium ? MobileEmail : DesktopEmail
  const ctaButtonVariant = isMedium ? 'ghost' : 'outline'
  const showCta = loanApplicatioCtaEnabled && loanApplicationUri

  return (
    <Card size='lg' display='flex' data-qa='Intro' justifyContent='center' flexDirection={['column', null, 'row']}>
      <div className={styles.avatarContainer}>
        <div className={styles.bgCircle} />
        <div className={styles.bgSmallCircle} />
        <div className={styles.avatarOverlap}>
          <Avatar className={classnames(styles.avatar, companyLogo && styles.avatarShifted)} src={photoUri} />
          {companyLogo && <Avatar className={classnames(styles.avatar, styles.avatarShifted)} src={companyLogo} />}
        </div>
      </div>
      <div className={styles.introExplain}>
        <div className={styles.header}>
          <div className={styles.stats}>
            <CustomerTitle className={styles.customerTitle} size='xxs' value={title} />
            <NMLS value={nmls} size='tiny' variant='neutralCool' variantCount='800' />
          </div>
          <Text weight='bold' variant='neutralCool' variantCount='900' size='m'>
            {customerName}
          </Text>
        </div>
        <Text className={styles.body} variant='neutralCool' variantCount='900' size='s'>
          {toName && intl.formatMessage(MSG.salutation, { firstName: toName })} {children}
        </Text>
        <div className={classnames(styles.contactInfo, isMedium && styles.contactInfoMobile)}>
          <PhoneComponent value={phone} />
          <EmailComponent value={email} />
        </div>
        {showCta && (
          <Flex direction='column'>
            <hr className={styles.ctaLineBreak} />
            <LeavingHomebotCtaButton
              ctaButtonSize='sm'
              ctaButtonColorScheme='primary'
              ctaButtonVariant={ctaButtonVariant}
              ctaLinkUrl={loanApplicationUri}
            >
              {intl.formatMessage(MSG.ctaButtonText)}
            </LeavingHomebotCtaButton>
          </Flex>
        )}
      </div>
    </Card>
  )
}
